import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, Loader2, Paperclip, X, FileText, Image as ImageIcon, CheckCircle2 } from "lucide-react";
import api from "@/lib/api";
import { useLang } from "@/i18n/LanguageContext";
import { LEGAL_UI } from "@/lib/legal";
import { PROJECT_CATEGORIES, BUDGET_RANGES, START_DATES, labelFor } from "@/lib/formOptions";
import { BookConsultation } from "@/components/BookConsultation";

const initial = {
  name: "", email: "", phone: "", project_type: "", budget: "",
  project_location: "", start_date: "", surface: "", message: "",
};
const SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY;
const ALLOWED_EXT = ["jpg", "jpeg", "png", "pdf", "heic", "heif"];
const MAX_SIZE = 10 * 1024 * 1024;
const MAX_FILES = 10;

const fmtSize = (b) => (b < 1024 * 1024 ? `${Math.round(b / 1024)} Ko` : `${(b / 1024 / 1024).toFixed(1)} Mo`);

const errMsg = (err, fallback) => {
  const d = err?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d[0]?.msg || fallback;
  return fallback;
};

export const ConsultationForm = ({ variant = "light" }) => {
  const { c, lang } = useLang();
  const f = c.form;
  const consentUi = (LEGAL_UI[lang] || LEGAL_UI.fr).consent;
  const [form, setForm] = useState(() => {
    const params = typeof window !== "undefined" ? new URLSearchParams(window.location.search) : null;
    const t = params ? params.get("type") || "" : "";
    const valid = PROJECT_CATEGORIES.some((o) => o.value === t);
    return { ...initial, project_type: valid ? t : "" };
  });
  const [consent, setConsent] = useState(false);
  const [company, setCompany] = useState(""); // honeypot
  const [token, setToken] = useState("");
  const [loading, setLoading] = useState(false);
  const [files, setFiles] = useState([]); // {id, original_filename, size, content_type}
  const [uploading, setUploading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const captchaRef = useRef(null);
  const widgetId = useRef(null);
  const fileInputRef = useRef(null);

  const set = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  useEffect(() => {
    if (!SITE_KEY) return undefined;
    const render = () => {
      if (!window.turnstile || !captchaRef.current || widgetId.current !== null) return;
      widgetId.current = window.turnstile.render(captchaRef.current, {
        sitekey: SITE_KEY,
        callback: (t) => setToken(t),
        "expired-callback": () => setToken(""),
        "error-callback": () => setToken(""),
      });
    };
    const existing = document.querySelector('script[data-turnstile="true"]');
    if (existing) {
      window.turnstile?.ready(render);
    } else {
      const s = document.createElement("script");
      s.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
      s.async = true; s.defer = true; s.dataset.turnstile = "true";
      s.onload = () => window.turnstile.ready(render);
      document.head.appendChild(s);
    }
    return () => { if (widgetId.current !== null && window.turnstile) { window.turnstile.remove(widgetId.current); widgetId.current = null; } };
  }, []);

  const onFilesSelected = async (e) => {
    const selected = Array.from(e.target.files || []);
    e.target.value = ""; // allow re-selecting the same file
    if (!selected.length) return;
    if (files.length + selected.length > MAX_FILES) { toast.error(f.tooManyFiles); return; }
    setUploading(true);
    for (const file of selected) {
      const ext = file.name.includes(".") ? file.name.split(".").pop().toLowerCase() : "";
      if (!ALLOWED_EXT.includes(ext)) { toast.error(`${file.name} — ${f.fileType}`); continue; }
      if (file.size > MAX_SIZE) { toast.error(`${file.name} — ${f.fileTooLarge}`); continue; }
      const fd = new FormData();
      fd.append("file", file);
      try {
        const { data } = await api.post("/consultations/upload", fd);
        setFiles((prev) => [...prev, data]);
      } catch (err) {
        toast.error(errMsg(err, f.error));
      }
    }
    setUploading(false);
  };

  const removeFile = (id) => setFiles((prev) => prev.filter((x) => x.id !== id));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.project_type || !form.project_location || !form.message) {
      toast.error(f.required); return;
    }
    if (!consent) { toast.error(consentUi.required); return; }
    if (SITE_KEY && !token) { toast.error(f.captcha || "Veuillez valider la vérification."); return; }
    setLoading(true);
    try {
      await api.post("/consultations", {
        ...form, file_ids: files.map((x) => x.id), consent, company, turnstile_token: token || null,
        site_origin: typeof window !== "undefined" ? window.location.origin : "", lang,
      });
      setSubmitted(true);
      setForm(initial); setConsent(false); setToken(""); setFiles([]);
      if (widgetId.current !== null) window.turnstile?.reset(widgetId.current);
    } catch (err) {
      toast.error(errMsg(err, f.error));
    } finally {
      setLoading(false);
    }
  };

  const dark = variant === "dark";
  const labelCls = dark ? "text-white/50" : "text-olime-stone";
  const fieldCls = dark
    ? "field-line !border-white/25 !text-white placeholder:!text-white/40 focus:!border-white"
    : "field-line";
  const consentText = dark ? "text-white/70" : "text-olime-muted";

  if (submitted) {
    return (
      <div data-testid="consultation-confirmation" className={`text-center py-6 ${dark ? "text-white" : "text-olime-ink"}`}>
        <CheckCircle2 size={44} strokeWidth={1.2} className={`mx-auto ${dark ? "text-white" : "text-olime-stone"}`} />
        <h3 className="font-serif text-3xl md:text-4xl mt-6">{f.confirmTitle}</h3>
        <p className={`mt-4 max-w-md mx-auto leading-relaxed ${dark ? "text-white/70" : "text-olime-muted"}`}>{f.confirmMessage}</p>
        <div className="mt-9 flex flex-wrap gap-4 justify-center">
          <Link to="/portfolio" data-testid="confirmation-portfolio-link" className={dark ? "btn-light" : "btn-outline"}>{f.confirmPortfolio} <ArrowRight size={14} /></Link>
          <BookConsultation variant={dark ? "light" : "ink"} testId="confirmation-book-cta" />
        </div>
        <button type="button" data-testid="confirmation-another" onClick={() => setSubmitted(false)} className={`mt-8 text-xs uppercase tracking-[0.2em] link-underline ${dark ? "text-white/60" : "text-olime-muted"}`}>
          {f.another}
        </button>
      </div>
    );
  }

  return (
    <form data-testid="consultation-form" onSubmit={onSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-8">
      <div>
        <label className={`eyebrow ${labelCls}`}>{f.name} *</label>
        <input data-testid="form-name" value={form.name} onChange={set("name")} className={`${fieldCls} mt-2`} placeholder={f.namePh} />
      </div>
      <div>
        <label className={`eyebrow ${labelCls}`}>{f.email} *</label>
        <input data-testid="form-email" type="email" value={form.email} onChange={set("email")} className={`${fieldCls} mt-2`} placeholder={f.emailPh} />
      </div>
      <div>
        <label className={`eyebrow ${labelCls}`}>{f.phone}</label>
        <input data-testid="form-phone" value={form.phone} onChange={set("phone")} className={`${fieldCls} mt-2`} placeholder={f.phonePh} />
      </div>
      <div>
        <label className={`eyebrow ${labelCls}`}>{f.projectType} *</label>
        <select data-testid="form-project-type" value={form.project_type} onChange={set("project_type")} className={`${fieldCls} mt-2`}>
          <option value="">{f.select}</option>
          {PROJECT_CATEGORIES.map((o) => <option key={o.value} value={o.value}>{labelFor(o, lang)}</option>)}
        </select>
      </div>
      <div>
        <label className={`eyebrow ${labelCls}`}>{f.budget}</label>
        <select data-testid="form-budget" value={form.budget} onChange={set("budget")} className={`${fieldCls} mt-2`}>
          <option value="">{f.select}</option>
          {BUDGET_RANGES.map((o) => <option key={o.value} value={o.value}>{labelFor(o, lang)}</option>)}
        </select>
      </div>
      <div>
        <label className={`eyebrow ${labelCls}`}>{f.projectLocation} *</label>
        <input data-testid="form-location" value={form.project_location} onChange={set("project_location")} className={`${fieldCls} mt-2`} placeholder={f.locationPh} />
      </div>
      <div>
        <label className={`eyebrow ${labelCls}`}>{f.startDate}</label>
        <select data-testid="form-start-date" value={form.start_date} onChange={set("start_date")} className={`${fieldCls} mt-2`}>
          <option value="">{f.select}</option>
          {START_DATES.map((o) => <option key={o.value} value={o.value}>{labelFor(o, lang)}</option>)}
        </select>
      </div>
      <div>
        <label className={`eyebrow ${labelCls}`}>{f.surface}</label>
        <input data-testid="form-surface" value={form.surface} onChange={set("surface")} className={`${fieldCls} mt-2`} placeholder={f.surfacePh} />
      </div>

      <div className="md:col-span-2">
        <label className={`eyebrow ${labelCls}`}>{f.message} *</label>
        <textarea data-testid="form-message" value={form.message} onChange={set("message")} rows={6} className={`${fieldCls} mt-2 resize-none`} placeholder={f.messagePh} />
      </div>

      {/* File upload */}
      <div className="md:col-span-2">
        <label className={`eyebrow ${labelCls}`}>{f.attachments}</label>
        <p className={`text-xs mt-1 ${dark ? "text-white/45" : "text-olime-muted"}`}>{f.attachmentsHint}</p>
        <input ref={fileInputRef} type="file" multiple accept=".jpg,.jpeg,.png,.pdf,.heic,.heif,image/jpeg,image/png,application/pdf,image/heic,image/heif" onChange={onFilesSelected} className="hidden" data-testid="form-file-input" />
        <button type="button" data-testid="form-file-add" onClick={() => fileInputRef.current?.click()} disabled={uploading || files.length >= MAX_FILES}
          className={`mt-3 inline-flex items-center gap-2 px-5 py-2.5 text-xs uppercase tracking-[0.18em] border transition-colors duration-300 disabled:opacity-50 ${dark ? "border-white/30 text-white hover:bg-white hover:text-olime-ink" : "border-olime-ink text-olime-ink hover:bg-olime-ink hover:text-olime-bg"}`}>
          {uploading ? <Loader2 size={14} className="animate-spin" /> : <Paperclip size={14} />} {uploading ? f.uploading : f.attachmentsAdd}
        </button>
        {files.length > 0 && (
          <ul data-testid="form-file-list" className="mt-4 space-y-2">
            {files.map((file) => (
              <li key={file.id} data-testid={`form-file-${file.id}`} className={`flex items-center justify-between gap-3 px-4 py-2.5 text-sm ${dark ? "bg-white/5 text-white/85" : "bg-olime-bg text-olime-ink"}`}>
                <span className="inline-flex items-center gap-2 truncate">
                  {file.content_type === "application/pdf" ? <FileText size={15} className="shrink-0 text-olime-stone" /> : <ImageIcon size={15} className="shrink-0 text-olime-stone" />}
                  <span className="truncate">{file.original_filename}</span>
                  <span className={`shrink-0 text-xs ${dark ? "text-white/40" : "text-olime-muted"}`}>· {fmtSize(file.size)}</span>
                </span>
                <button type="button" data-testid={`form-file-remove-${file.id}`} onClick={() => removeFile(file.id)} className="shrink-0 text-olime-muted hover:text-red-500 transition-colors"><X size={16} /></button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Honeypot — hidden from users */}
      <input type="text" tabIndex={-1} autoComplete="off" value={company} onChange={(e) => setCompany(e.target.value)} className="hidden" aria-hidden="true" data-testid="form-company-hp" />

      <div className="md:col-span-2">
        <label className={`flex items-start gap-3 cursor-pointer text-sm ${consentText}`}>
          <input type="checkbox" data-testid="form-consent" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-1 w-4 h-4 accent-olime-ink" />
          <span>
            {consentUi.label}{" "}
            <Link to="/legal/confidentialite" data-testid="form-consent-link" className={`underline ${dark ? "text-white" : "text-olime-ink"}`}>{consentUi.link}</Link>
          </span>
        </label>
      </div>

      {SITE_KEY && <div className="md:col-span-2" ref={captchaRef} data-testid="form-captcha" />}

      <div className="md:col-span-2">
        <button type="submit" data-testid="form-submit" disabled={loading || uploading} className={`${dark ? "btn-light" : "btn-ink"} w-full md:w-auto disabled:opacity-60`}>
          {loading ? <Loader2 size={14} className="animate-spin" /> : <ArrowRight size={14} />}
          {c.cta.startProject}
        </button>
      </div>
    </form>
  );
};
